import { useDispatch, useSelector } from "react-redux";
import { useCallback } from "react";
import { enqueueSnackbar } from "notistack";
import {
  fetchNotificacionesUsuario,
  markNotificacionesAsViewed,
  setTimerRef,
  clearTimerRef,
  setUnreadCount,
} from "../Slices/NotificacionesUsuario/notificacionesUsuarioSlice";
import { DynamicIcon } from "../Components/DynamicIcon/DynamicIcon";
import { storageService } from "Services/storage_service";
import { ROLES } from "../Constantes/constants";

const NOTIFICATION_INTERVAL = 1 * 60 * 1000; // 1 minuto en milisegundos

export const useNotificationTimer = () => {
  const dispatch = useDispatch();
  const { timerRef } = useSelector((state) => state.notificacionesUsuario);

  const calculateRelativeTime = (dateString) => {
    const now = new Date();
    const notificationDate = new Date(dateString);
    const diffInMinutes = Math.floor((now - notificationDate) / (1000 * 60));

    if (diffInMinutes < 1) return "Ahora";
    if (diffInMinutes < 60)
      return `Hace ${diffInMinutes} minuto${diffInMinutes !== 1 ? "s" : ""}`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24)
      return `Hace ${diffInHours} hora${diffInHours !== 1 ? "s" : ""}`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7)
      return `Hace ${diffInDays} día${diffInDays !== 1 ? "s" : ""}`;

    const diffInWeeks = Math.floor(diffInDays / 7);
    return `Hace ${diffInWeeks} semana${diffInWeeks !== 1 ? "s" : ""}`;
  };

  const showNotificationSnackbars = (notifications) => {
    const unshownNotifications = notifications.filter(
      (notification) => !notification.snackbarMostrado
    );

    if (unshownNotifications.length === 0) return;

    unshownNotifications.forEach((notification) => {
      const relativeTime = notification.fechaEntrega
        ? calculateRelativeTime(notification.fechaEntrega)
        : "Ahora";

      enqueueSnackbar("", {
        key: notification.idNotificacionUsuario,
        preventDuplicate: true,
        variant: "notification",
        autoHideDuration: 5000,
        notificationData: {
          idNotificacionUsuario: notification.idNotificacionUsuario,
          icon: DynamicIcon,
          icono: notification.icono,
          titulo: notification.titulo,
          descripcion: notification.descripcion,
          time: relativeTime,
          isRecent: true,
        },
      });
    });

    const notificationsToUpdate = unshownNotifications.map((notification) => ({
      idNotificacionUsuario: notification.idNotificacionUsuario,
      snackbarMostrado: true,
    }));

    dispatch(markNotificacionesAsViewed(notificationsToUpdate));
  };

  const fetchAndShowNotifications = async () => {
    const cookieUser = storageService.getItem("user");

    if (!cookieUser?.idUsuario) return;

    try {
      const result = await dispatch(
        fetchNotificacionesUsuario(cookieUser?.idUsuario)
      );

      if (result.payload && Array.isArray(result.payload)) {
        showNotificationSnackbars(result.payload);

        const unreadNotificationCount = result.payload.filter(
          (notification) => !notification.fechaLectura
        ).length;

        dispatch(setUnreadCount(unreadNotificationCount));
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const startNotificationTimer = () => {
    const cookieUser = storageService.getItem("user");

    if (timerRef) {
      clearTimeout(timerRef);
      dispatch(clearTimerRef());
    }

    if (
      !cookieUser?.token ||
      !cookieUser?.idUsuario ||
      cookieUser.idRol !== ROLES.NO_CLIENTE.ID
    ) {
      return;
    }

    fetchAndShowNotifications();

    const timerId = setInterval(() => {
      fetchAndShowNotifications();
    }, NOTIFICATION_INTERVAL);

    dispatch(setTimerRef(timerId));
  };

  const clearNotificationTimer = () => {
    dispatch(clearTimerRef());
  };

  const triggerNotificationFetch = useCallback(() => {
    startNotificationTimer();
  }, []);

  return {
    startNotificationTimer,
    clearNotificationTimer,
    triggerNotificationFetch,
  };
};
